import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  UserGroupIcon,
  WrenchScrewdriverIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import SelectMenu from '../ui/SelectMenu';
import { Checkbox } from '../ui/Checkbox';
import Slideover from '../ui/Slideover';
import { useConfirm } from '../ui/ConfirmationModal';
import TypeaheadChips from '../ui/TypeaheadChips';
import { WORangePicker } from './WORangePicker';
import { ConflictWarnings } from './ConflictWarnings';
import { InlineCreate } from './InlineCreate';
import { detectConflicts, workOrderHasValidRange } from '../../utils/workOrderModel';
import { useSettingsContext } from '../../hooks/useSettings';
import { workOrderLabel } from '../../utils/displayId';
import { resolveCrewColor } from '../../utils/crewColors';
import { scheduleWrite, scheduleError } from '../../utils/woSchedule';
import { projectLineItemsToWOMaterials } from '../../quote-builder/woMaterialsProjection';
import { WO_TYPE_REQUIRED_MSG } from '../../utils/workOrderEdit';
import { lineQuantityText } from '../../utils/lineQuantity';
import { attachmentIdOf, entryDisplay } from '../../utils/attachmentEntry';

// ─── WO Create Side-Sheet ────────────────────────────────────────────────────
// PT-22 Wave W1 — atoms + seed types + field order + WO # / title.
// Conforms to WO-1 §125 field order. Consumes Slideover / SelectMenu / Checkbox
// atoms as-is. WO Type sourced from vocabSeeds via useSettings.getOptions.
//
// §1.1 single range picker · §1.2 default times · §1.3 holidays ·
// WO-1 §130 (EDIT 2026-07-10, D-e/OQ3): ONE WO PER LINE ITEM — once assigned,
//   a line item LEAVES the pool: dimmed, locked, NOT selectable. The earlier
//   §1.4 "multiple WOs per line item" model is retired.
// §1.5 inline create · §1.7 inline conflicts.
//
// Data payload — Option D dual-write (2026-07-25):
//   Emit crewId + techIds + resourceIds AND crew + techs + resources snapshot.
//   Downstream views prefer IDs, fall back to names. See plan §Data payload decision.

const asArray = (v) => (Array.isArray(v) ? v : []);

// Which line items are already covered by an existing WO (WO-1 §130).
// Returns a map { [lineItemId]: coveringWO } so callers can render the WO #
// alongside the lock. Prior shape was a Set (id-only) which dropped the WO
// reference — upgraded per FP2-1.
function coverageMapFromWorkOrders(existingWorkOrders = []) {
  const map = {};
  asArray(existingWorkOrders).forEach((wo) => {
    asArray(wo.lineItemIds || wo.line_item_ids).forEach((id) => {
      if (id != null && !(String(id) in map)) map[String(id)] = wo;
    });
  });
  return map;
}

// Pre-generate WO # / title as `job#-seq` (1e (ii)). Sequence is 1 + existing count.
function nextWOTitle(job, existingWorkOrders) {
  const jobNum = job?.jobNumber || job?.job_number || job?.id || 'JOB';
  const seq = asArray(existingWorkOrders).length + 1;
  return `${jobNum}-${String(seq).padStart(2, '0')}`;
}

// Suggest a WO title from selected line items' area/scope (DATA D-8).
// Falls back gracefully — if items lack area/scope, return null so the pre-gen sticks.
function suggestTitleFromItems(items) {
  if (!items || items.length === 0) return null;
  const first = items[0];
  const area = first.area || first.location || first.zone;
  const scope = first.scope || first.description || first.item_name;
  if (!area && !scope) return null;
  const parts = [area, scope].filter(Boolean).map((s) => String(s).trim()).filter(Boolean);
  return parts.length ? parts.join(' — ').slice(0, 80) : null;
}

// PR-72 Commit 5 (R-90, dispatch 418 §2): payload assembly extracted so
// it is testable as a pure function. See WOCreateSheets.payload.test.js.
//
// Option D dual-write (2026-07-25) — IDs authoritative, name snapshot for
// display. PT-22 W2 extended this to techs. The inactive assign branch is
// emitted with empty values so downstream can reason about assign shape
// from a single payload (no undefined vs. null ambiguity).
// IDI-55: the existing files a new WO can carry: the job's own plus its
// converted quote's (job GET `quoteDocuments`, read at view time). Keyed by
// attachmentIdOf (Shape-B refs carry attachmentId, legacy entries id),
// de-duplicated, id-less entries dropped (they cannot be selected).
export function woCreateDocumentPool(job) {
  const seen = new Set();
  const out = [];
  [...asArray(job?.documents), ...asArray(job?.quoteDocuments)].forEach((d) => {
    const key = attachmentIdOf(d);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push({ key, label: entryDisplay(d).name || key, doc: d });
  });
  return out;
}

export function buildWOSubmitPayload({
  form,
  job,
  lineItems = [],
  crewOptions = [],
  techPool = [],
  resourceOptions = [],
  creator = null,
  now = () => new Date(),
}) {
  const inCrewMode = form.assignMode === 'crew';
  const selectedCrew = inCrewMode ? crewOptions.find((c) => c.id === form.crewId) : null;
  const selectedTechs = !inCrewMode
    ? asArray(techPool).filter((t) => asArray(form.techIds).includes(t.value))
    : [];
  const selectedResources = resourceOptions.filter((r) => form.resourceIds.includes(r.id));
  const selectedDocuments = woCreateDocumentPool(job).filter((p) =>
    asArray(form.documentIds).includes(p.key)
  );
  // B5 (2026-07-27) supersedes G1: seed WO materials via a pure projection
  // module. See quote-builder/woMaterialsProjection.js.
  const selectedLineItems = lineItems.filter((it, idx) => form.lineItemIds.includes(it.id || idx));
  const seededMaterials = projectLineItemsToWOMaterials(selectedLineItems);
  const { titleTouched, documentIds: _dIds, ...persist } = form;
  return {
    ...persist,
    // PR-72 Commit 5 (R-90, dispatch 418 §2 writer fix): emit
    // scheduledDate alongside startDate/endDate. Canonical for WO
    // records is scheduledDate (cmd/jobs/main.go:504-507 — "frontend
    // reads scheduledDate"). The reader accessor `getScheduledDate` in
    // canonicalFields.js still tolerates startDate as an alias to heal
    // WOs written before this fix, but writing both here stops the
    // divergence growing.
    //
    // Range-vs-day: RESOLVED (dispatch 419 §2). scheduledDate is the
    // START of the WO; endDate carries the SPAN; startDate is a
    // redundant spelling of scheduledDate. No writer in the corpus
    // (this sheet, rescheduleWorkOrder, enricher) produces a WO where
    // scheduledDate and startDate differ. See the getScheduledDate
    // docblock for the writer-evidence table.
    scheduledDate: form.startDate,
    // IDI-471 + U-13: also stamp absolute start/stop instants, so a multi-day
    // WO's stop is unambiguous to every reader and to auto-complete (IDI-466).
    ...scheduleWrite({
      startDate: form.startDate, startTime: form.startTime,
      stopDate: form.endDate || form.startDate, stopTime: form.endTime,
    }),
    title: (form.title || '').trim(),
    jobId: job?.id,
    status: 'active',
    crewId: inCrewMode ? form.crewId : '',
    crew: inCrewMode ? (selectedCrew?.name || '') : '',
    crewName: inCrewMode ? (selectedCrew?.name || '') : '',
    techIds: inCrewMode ? [] : asArray(form.techIds),
    techs: inCrewMode ? [] : selectedTechs.map((t) => ({ id: t.value, name: t.label })),
    resourceIds: form.resourceIds,
    resources: selectedResources.map((r) => ({ id: r.id, name: r.name, category: r.category })),
    documentIds: asArray(form.documentIds),
    documents: selectedDocuments.map((p) => ({
      id: p.key,
      filename: p.label,
    })),
    materials: seededMaterials,
    // IDI-551 (Adrian 10-21): the note typed here is the WO's FIRST
    // ATTRIBUTED note, by the creator — never a bare string.
    notes: woCreateNotes(form.notes, creator, now),
  };
}

/** The create sheet's "Notes (optional)" as the WO's notes list (IDI-551). */
function woCreateNotes(text, creator, now = () => new Date()) {
  const content = typeof text === 'string' ? text.trim() : '';
  if (!content) return [];
  const at = now();
  return [{
    id: `note-${at.getTime()}`,
    content,
    author: creator?.name || creator?.email || 'Team Member',
    author_id: creator?.id || null,
    created_at: at.toISOString(),
  }];
}

/**
 * The Create sheet's required-field check, as a pure function (IDI-517 tests
 * it directly). Returns { field: message } for every failing field.
 */
export function validateWOCreateForm(form) {
  const newErrors = {};
  if (!form.title || !form.title.trim()) newErrors.title = 'WO # / title is required';
  // IDI-517: a work-order type is required (it drives reporting).
  if (!form.woType) newErrors.woType = WO_TYPE_REQUIRED_MSG;
  // §2: a WO cannot persist without a valid range
  if (!workOrderHasValidRange(form)) newErrors.startDate = 'A start and stop date are required';
  // IDI-467 (D-02): a scheduled work order needs a stop TIME too (it
  // completes itself then, IDI-466). The dealer's default end time
  // pre-fills it when set.
  else if (!form.endTime) newErrors.startDate = 'A stop time is required';
  else {
    const order = scheduleError({ startDate: form.startDate, startTime: form.startTime, stopDate: form.endDate, stopTime: form.endTime });
    if (order) newErrors.startDate = order;
  }
  // PT-22 W2: validate the active assign branch. Either crew OR at least
  // one tech; not both required. Error surfaces on whichever branch is
  // currently selected so the message points at the visible input.
  if (form.assignMode === 'crew') {
    if (!form.crewId) newErrors.crewId = 'Crew is required';
  } else {
    if (asArray(form.techIds).length === 0) newErrors.techIds = 'Select at least one tech';
  }
  if (form.lineItemIds.length === 0) newErrors.lineItemIds = 'Select at least one line item';
  return newErrors;
}

export function WOCreateSheet({
  open, onClose, onSubmit, job,
  crews = [], resources = [], techs = [],
  existingWorkOrders = [], pto = [],
  schedulingDefaults = {},
  preCheckedLineItemIds = [],
  onViewConflict,
  creator = null,
}) {
  const { getOptions, getEnumLabel } = useSettingsContext();
  const confirm = useConfirm();
  const woTypeOptions = useMemo(() => {
    const opts = getOptions ? getOptions('work_order_types') : [];
    return asArray(opts).filter((o) => !o.hidden);
  }, [getOptions]);

  const initialTitle = useMemo(() => nextWOTitle(job, existingWorkOrders), [job, existingWorkOrders]);

  const [form, setForm] = useState({
    title: '',
    titleTouched: false, // 1e(ii): once user edits, stop auto-suggesting
    woType: '',
    startDate: '', endDate: '', startTime: '', endTime: '',
    // PT-22 W2: Assign toggle. 'crew' | 'tech'. Determines which of
    // crewId / techIds is authoritative for validation + payload emission.
    assignMode: 'crew',
    crewId: '',
    techIds: [],
    resourceIds: [],
    lineItemIds: [],
    /* PT-22 W3 §3.2: documentIds (IDs pointing at job.documents) replaces
       raw File[] anti-pattern (TD-1). Payload emit derives `documents`
       snapshot at submit time. */
    documentIds: [],
    notes: '',
  });
  const [crewOptions, setCrewOptions] = useState(crews);
  const [resourceOptions, setResourceOptions] = useState(resources);
  // PT-22 W2: tech options are read-only (no create affordance per plan —
  // '+ Invite tech' is E5 deferred). Keep as state for symmetry with the
  // crews/resources pattern in case a follow-up wires inline invite.
  const [techPool, setTechPool] = useState(techs);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const initialFocusRef = useRef(null);

  const lineItems = asArray(job?.line_items || job?.lineItems);
  const coverageMap = useMemo(() => coverageMapFromWorkOrders(existingWorkOrders), [existingWorkOrders]);

  // Seed defaults + pre-checked items on open (§1.2, §1.4 follow-on WO pre-check).
  useEffect(() => {
    if (open) {
      setForm((prev) => ({
        ...prev,
        title: prev.title || initialTitle,
        startTime: prev.startTime || schedulingDefaults.startTime || '08:00',
        endTime: prev.endTime || schedulingDefaults.endTime || '17:00',
        lineItemIds: prev.lineItemIds.length ? prev.lineItemIds : asArray(preCheckedLineItemIds),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { setCrewOptions(crews); }, [crews]);
  useEffect(() => { setResourceOptions(resources); }, [resources]);
  useEffect(() => { setTechPool(techs); }, [techs]);

  // 1e(ii) auto-suggest: recompute title from selected items until user manually edits.
  useEffect(() => {
    if (!open) return;
    if (form.titleTouched) return;
    const selected = lineItems.filter((it, idx) => form.lineItemIds.includes(it.id || idx));
    const suggested = suggestTitleFromItems(selected);
    if (suggested) {
      setForm((prev) => (prev.titleTouched ? prev : { ...prev, title: `${initialTitle} · ${suggested}` }));
    } else {
      setForm((prev) => (prev.titleTouched ? prev : { ...prev, title: initialTitle }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.lineItemIds, initialTitle, open]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleTitleChange = (value) => {
    setForm((prev) => ({ ...prev, title: value, titleTouched: true }));
    if (errors.title) setErrors((prev) => ({ ...prev, title: null }));
  };

  const setRange = (range) => setForm((prev) => ({ ...prev, ...range }));

  const toggleLineItem = (itemId) => {
    // WO-1 §130: covered items are locked — defensive guard even if UI leaks a click.
    if (coverageMap[String(itemId)]) return;
    setForm((prev) => ({
      ...prev,
      lineItemIds: prev.lineItemIds.includes(itemId)
        ? prev.lineItemIds.filter((id) => id !== itemId)
        : [...prev.lineItemIds, itemId],
    }));
  };

  // PT-22 W3 Amendment A: `toggleResource` was the checkbox-list toggler.
  // Removed — TypeaheadChips owns add/remove via its onChange contract.

  // §1.7: proactive, inline, non-blocking conflict warnings at assignment time
  const conflicts = useMemo(() => {
    if (!form.startDate) return [];
    return detectConflicts(
      {
        startDate: form.startDate,
        endDate: form.endDate || form.startDate,
        // PT-22 W2: pass whichever branch is active. Crew branch emits its
        // crewId; tech branch emits techIds. detectConflicts consumes both
        // (see utils/workOrderModel).
        crewIds: form.assignMode === 'crew' && form.crewId ? [form.crewId] : [],
        techIds: form.assignMode === 'tech' ? asArray(form.techIds) : [],
        resourceIds: form.resourceIds,
      },
      { existing: existingWorkOrders, pto }
    );
  }, [form.assignMode, form.startDate, form.endDate, form.crewId, form.techIds, form.resourceIds, existingWorkOrders, pto]);

  // PT-22 W2 (retain-inactive toggle): isDirty reflects EMITTED state,
  // not retained state. A crew selection retained while the tech branch is
  // active would NOT be emitted (handleSubmit gates on assignMode), so it
  // must not count toward dirty — otherwise a user who toggles
  // Crew→Tech→Crew with no selections gets a spurious discard prompt from
  // residue. Reads the visible branch only.
  const isDirty = useMemo(() => {
    const assignDirty = form.assignMode === 'crew'
      ? !!form.crewId
      : asArray(form.techIds).length > 0;
    return (
      form.woType ||
      form.startDate ||
      form.endDate ||
      assignDirty ||
      form.resourceIds.length > 0 ||
      form.lineItemIds.length > 0 ||
      form.notes ||
      form.documentIds.length > 0 ||
      form.titleTouched
    );
  }, [form]);

  // W1-BUG-1: reset must fire on any real close (discard OR post-submit),
  // never on "Keep editing". Lifted from handleSubmit so both paths converge.
  const resetForm = () => {
    setForm({
      title: '', titleTouched: false, woType: '',
      startDate: '', endDate: '', startTime: '', endTime: '',
      assignMode: 'crew',
      crewId: '', techIds: [],
      resourceIds: [], lineItemIds: [], documentIds: [], notes: '',
    });
    setErrors({});
  };

  const handleClose = async () => {
    if (isDirty) {
      const ok = await confirm({
        title: 'Discard this work order?',
        message: 'Unsaved changes will be lost.',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing',
        tone: 'danger',
      });
      if (!ok) return;
    }
    resetForm();
    onClose();
  };

  const validate = () => {
    const newErrors = validateWOCreateForm(form);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit(
        buildWOSubmitPayload({
          form,
          job,
          lineItems,
          crewOptions,
          techPool,
          resourceOptions,
          creator,
        })
      );
      // reset for next open
      resetForm();
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to create work order' });
    } finally {
      setSubmitting(false);
    }
  };

  // FP2-F3: crew renders as a dismissible pill w/ color swatch via TypeaheadChips.
  // One consistent chip treatment inside Assign (operator ruling). Options carry
  // `color` so the chip shows the crew swatch; TypeaheadChips renders it via F2.
  // FP3-1 F3: `color` resolved via the shared `resolveCrewColor` helper so
  // this picker's swatch always agrees with the Settings dot (both read the
  // same field with the same fallback — no drift). Handles the alpha root
  // cause where crews stored without `color` previously fell through to
  // `undefined` and rendered as a generic red pill.
  const crewChipOptions = useMemo(
    () =>
      asArray(crewOptions).map((c) => ({
        value: c.id,
        label: c.name,
        color: resolveCrewColor({ color: c.color || c.swatch }),
      })),
    [crewOptions]
  );

  // PT-22 W2: techPool is already in TypeaheadChips option shape
  // ({ value, label, hint }) courtesy of useTechOptions in the parent.
  const techChipOptions = useMemo(() => asArray(techPool), [techPool]);

  // PT-22 W3 Amendment A: resources are THINGS (equipment / vehicles /
  // tools), not people. Surface `<name> · <category>` (e.g. "f150 ·
  // Vehicles") in the picker + on the chip so operators read them as
  // equipment. Categories come from resource_category.v1 seed via the
  // instance's own `category` field written by ResourceTypesPage.
  // PT-22 W3 §3.2: documents type-ahead options come from job.documents
  // (already fetched into the job envelope by JobDetail.fetchJob). Shape:
  // { id, filename, ... }. E4-gap: fresh uploads deferred (backend endpoint
  // absent) — disabled "+ Add documents" button below.
  const documentChipOptions = useMemo(
    () =>
      woCreateDocumentPool(job).map((p) => ({
        value: p.key,
        label: p.label,
      })),
    [job]
  );

  const resourceChipOptions = useMemo(
    () =>
      asArray(resourceOptions).map((r) => {
        // FP2-F6: title-case seed categories via getEnumLabel; tenant custom values
        // pass through unchanged (getEnumLabel returns the raw value for unknown
        // keys under custom:true seeds). Fixes '202 Mid-Range · equipment' → '· Equipment'.
        const catLabel = r.category
          ? (getEnumLabel?.('resource_category', r.category) || r.category)
          : '';
        return {
          value: r.id,
          label: catLabel ? `${r.name} · ${catLabel}` : r.name,
          hint: r.identifier || undefined,
        };
      }),
    [resourceOptions, getEnumLabel]
  );

  const footer = (
    <div className="flex justify-end gap-3">
      <Button variant="secondary" onClick={handleClose}>Cancel</Button>
      <Button onClick={handleSubmit} loading={submitting}>Create Work Order</Button>
    </div>
  );

  return (
    <Slideover
      open={open}
      onClose={handleClose}
      title="Create Work Order"
      subtitle={job?.title || undefined}
      footer={footer}
      width="md"
      initialFocus={initialFocusRef}
    >
      <div className="space-y-5">
        {/* 1. Job (read-only) — WO-1 §125 field 1 */}
        <div className="bg-gray-50 px-4 py-3" style={{ borderRadius: 'var(--radius-md)' }}>
          <p className="text-xs font-medium text-gray-500 uppercase">Job</p>
          <p className="text-sm font-medium text-gray-900">{job?.title || 'Untitled Job'}</p>
          <p className="text-xs text-gray-500">{job?.customerName || job?.customer_name || ''}</p>
        </div>

        {/* 2. WO # / title (1e (ii)) — WO-1 §125 field 2 */}
        <div>
          <label className="label">WO # / title</label>
          <Input
            ref={initialFocusRef}
            value={form.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder={initialTitle}
            invalid={!!errors.title}
          />
          {errors.title && <p className="text-xs text-red-600 mt-1">{errors.title}</p>}
          {!form.titleTouched && (
            <p className="text-xs text-gray-400 mt-1">
              Auto-generated from job #{form.lineItemIds.length > 0 ? ' + selected items' : ''}. Editable.
            </p>
          )}
        </div>

        {/* 3. Type (1b + 1c) — WO-1 §125 field 3 */}
        <div>
          <label className="label">Work Order Type</label>
          <SelectMenu
            value={form.woType}
            onChange={(val) => handleChange('woType', val)}
            options={woTypeOptions}
            placeholder="Select type…"
            width="full"
            invalid={!!errors.woType}
          />
          {errors.woType && <p className="text-xs text-red-600 mt-1">{errors.woType}</p>}
        </div>

        {/* 4. Schedule — WO-1 §125 field 4 */}
        <WORangePicker
          value={{ startDate: form.startDate, endDate: form.endDate, startTime: form.startTime, endTime: form.endTime }}
          onChange={setRange}
          error={errors.startDate}
        />

        {/* 5. Assign — WO-1 §125 field 5. W2: segmented Crew | Individual tech(s) toggle.
            FP2-F3: crew branch renders TypeaheadChips (max=1) with color swatch for
            consistent chip treatment across Assign. InlineCreate kind="crew" retained
            (WO-1 §128 permanent). Tech branch shows TypeaheadChips multi-select.
            No tech InlineCreate — permanent per plan.
            '+ Invite tech' CTA omitted — E5-REVISED deferred (see docs/pt-22-w3-fix-pack-2/
            E5-REVISED-invite-tech-deferral.md for behavioral endpoint check). */}
        <div>
          <label className="label">
            <UserGroupIcon className="h-4 w-4 inline mr-1" />
            Assign
          </label>
          {/* Red segmented toggle. Inline (no atom rewrite per plan scope). */}
          <div
            role="tablist"
            aria-label="Assign to crew or individual techs"
            className="inline-flex rounded-md border border-[color:var(--color-border)] overflow-hidden mb-2"
          >
            {[
              { key: 'crew', label: 'Crew' },
              { key: 'tech', label: 'Individual tech(s)' },
            ].map((seg) => {
              const active = form.assignMode === seg.key;
              return (
                <button
                  key={seg.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    // Retain-inactive (operator ruling 2026-07-25): a mode
                    // toggle is navigational, not committal — fat-fingering
                    // to the other branch and back must not wipe a selection.
                    // The inactive branch's state is retained but NOT emitted:
                    // handleSubmit + isDirty + conflicts memo all gate on
                    // assignMode, so the payload matches the visible state.
                    // resetForm still clears BOTH branches (W1-BUG-1 rule);
                    // retention is within-session only.
                    setForm((prev) => ({ ...prev, assignMode: seg.key }));
                    // Clear branch validation errors so the visible input
                    // starts clean on the new branch. Non-branch errors kept.
                    setErrors((prev) => {
                      const { crewId: _c, techIds: _t, ...rest } = prev;
                      return rest;
                    });
                  }}
                  className={
                    (active
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50') +
                    ' px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-0'
                  }
                >
                  {seg.label}
                </button>
              );
            })}
          </div>

          {form.assignMode === 'crew' ? (
            <>
              <TypeaheadChips
                variant="identity"
                options={crewChipOptions}
                value={form.crewId ? [form.crewId] : []}
                onChange={(next) => {
                  // FP2-F3: single-select semantics inside a multi-select atom.
                  // Last picked wins; cleared → crewId=''. Keep the label snapshot
                  // in sync so downstream payloads carry both id + name (dual-write).
                  // FP3-1 F3: also stamp `crewColor` on the payload so the WO
                  // envelope carries a resolved, non-empty colour on create.
                  // Display sites (WorkOrderDetail line ~1219/~1271) then read
                  // `wo.crewColor` without a second Settings lookup, and stay
                  // consistent with the picker swatch that produced them
                  // (opt.color was already resolved via resolveCrewColor in
                  // crewChipOptions above).
                  const picked = asArray(next)[asArray(next).length - 1] || '';
                  handleChange('crewId', picked);
                  const opt = crewChipOptions.find((o) => o.value === picked);
                  handleChange('crew', opt?.label || '');
                  handleChange('crewColor', opt?.color || '');
                }}
                placeholder="Select crew…"
                emptyMessage={
                  crewChipOptions.length === 0
                    ? 'No crews on file'
                    : 'No matching crews'
                }
                allowCreate={false}
                max={1}
                invalid={!!errors.crewId}
                /* Remount on branch switch to reset the internal query. */
                key={form.assignMode}
              />
              {errors.crewId && <p className="text-xs text-red-600 mt-1">{errors.crewId}</p>}
              <div className="mt-2">
                <InlineCreate
                  kind="crew"
                  onCreated={(c) => {
                    setCrewOptions((prev) => [...prev, c]);
                    if (c.id) {
                      handleChange('crewId', c.id);
                      handleChange('crew', c.name || '');
                    }
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <TypeaheadChips
                options={techChipOptions}
                value={asArray(form.techIds)}
                onChange={(next) => handleChange('techIds', asArray(next))}
                placeholder="Search techs…"
                emptyMessage={
                  techChipOptions.length === 0
                    ? 'No field techs on file'
                    : 'No matching techs'
                }
                allowCreate={false}
                /* PT-22 W3 §2: remount on branch switch drops internal query
                   useState('') without touching parent-owned form.techIds. */
                key={form.assignMode}
              />
              {errors.techIds && <p className="text-xs text-red-600 mt-1">{errors.techIds}</p>}
              {/* '+ Invite tech' CTA intentionally omitted — E5 deferred. */}
            </>
          )}
        </div>

        {/* §1.7 inline, non-blocking conflict warnings */}
        <ConflictWarnings warnings={conflicts} onViewConflict={onViewConflict} />

        {/* 6. Line items — WO-1 §125 field 6 (moved above Resources).
            WO-1 §130 (EDIT 2026-07-10, D-e/OQ3): covered line items are locked & NOT selectable. */}
        <div>
          <label className="label">Quote Line Items</label>
          {errors.lineItemIds && <p className="text-xs text-red-600 mb-1">{errors.lineItemIds}</p>}
          <div
            className="space-y-2 mt-1 max-h-48 overflow-y-auto border p-2"
            style={{ borderRadius: 'var(--radius-md)' }}
          >
            {lineItems.map((item, idx) => {
              const itemId = item.id || idx;
              const coveringWO = coverageMap[String(itemId)];
              const isCovered = Boolean(coveringWO);
              const coveringWoLabel = isCovered
                ? workOrderLabel(coveringWO) // IDI-411: never the raw id
                : null;
              const checked = form.lineItemIds.includes(itemId);
              return (
                <div
                  key={itemId}
                  className={`flex items-start gap-2 p-1.5 rounded hover:bg-gray-50 ${isCovered ? 'opacity-60' : ''}`}
                >
                  <Checkbox
                    checked={checked}
                    disabled={isCovered}
                    onChange={() => toggleLineItem(itemId)}
                    aria-label={isCovered ? `Locked — already on ${coveringWoLabel}` : 'Select quote item'}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 truncate">
                      {item.description || item.item_name}
                      {isCovered && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 text-xs"
                          style={{ color: 'var(--color-text-muted)' }}
                          title={`Already on ${coveringWoLabel}`}
                        >
                          <LockClosedIcon className="h-3 w-3" aria-hidden="true" />
                          <span>{coveringWoLabel}</span>
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">Qty: {lineQuantityText(item)}</p>
                  </div>
                </div>
              );
            })}
            {lineItems.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-2">No line items on this job</p>
            )}
          </div>
        </div>

        {/* 7. Resources — WO-1 §125 field 7 (moved below line items). W3: type-ahead over the tenant's Settings resource list; chip renders as "f150 · Vehicles" per Amendment A. */}
        <div>
          <label className="label">
            <WrenchScrewdriverIcon className="h-4 w-4 inline mr-1" />
            Resources (optional)
          </label>
          <TypeaheadChips
            options={resourceChipOptions}
            value={asArray(form.resourceIds)}
            onChange={(next) => handleChange('resourceIds', asArray(next))}
            placeholder="Search resources…"
            emptyMessage={
              resourceChipOptions.length === 0
                ? 'No resources on file'
                : 'No matching resources'
            }
            allowCreate={false}
          />
          <div className="mt-2">
            <InlineCreate
              kind="resource"
              onCreated={(r) => {
                setResourceOptions((prev) => [...prev, r]);
                handleChange('resourceIds', [...asArray(form.resourceIds), r.id]);
              }}
            />
          </div>
        </div>

        {/* 8. Attachments — WO-1 §125 field 8 (moved above Notes). W3: multi-select over existing job files (job.documents). Fresh uploads deferred — E4 endpoint absent.
            Terminology (grounding §7, 2026-07-25): 'Files' = browsable store (this
            Job's Files tab, Settings Files repo). 'Attachments' = files bound to a
            specific record (WO/Quote/Invoice/CO). Same 'Attachment' entity (E-017);
            the label follows the user's relationship to the record, not the storage.
            Do not "correct". */}
        <div>
          <label className="label">Attachments (optional)</label>
          <TypeaheadChips
            options={documentChipOptions}
            value={asArray(form.documentIds)}
            onChange={(next) => handleChange('documentIds', asArray(next))}
            placeholder="Attach existing job files…"
            emptyMessage={
              documentChipOptions.length === 0
                ? 'No files on this job yet'
                : 'No matching files'
            }
            allowCreate={false}
          />
          <div className="mt-2">
            {/* PT-53b commit 1: attach-during-create is NOT wired to AttachModal.
              * The AttachModal component + state machine are built and
              * unit-tested (see `components/attachments/AttachModal.js` +
              * its .test), but this surface can't consume it usefully today:
              * the WO id doesn't exist until submit, so createAttachment
              * can't run. The right pattern is a two-phase flow — (a) hold
              * picked refs in local state pre-create, (b) after successful
              * WO creation, run createAttachment against the new WO id +
              * append to wo.attachments[]. That's a commit-2 concern.
              *
              * For commit 1 the disabled stub + honest copy stay as-is.
              * Post-create attach lives on WorkOrderDetail's Attachments
              * card, which IS a commit-1 consumer.
              *
              * See operator ruling 2026-08-02 + `Iterations/Iteration_19/PRs/00_NEVADO_BUILD_ATTACHMODAL_2026-08-02.md`.
              */}
            <button
              type="button"
              disabled
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-[color:var(--color-border)] px-2 py-0.5 text-xs text-gray-400 cursor-not-allowed"
              title="Upload pending — E4 endpoint deferred"
            >
              <span aria-hidden="true">+</span> Add documents
            </button>
            <p className="text-xs text-gray-400 mt-1">
              Upload pending — attach existing job files above.
            </p>
          </div>
        </div>

        {/* 9. Notes — WO-1 §125 field 9 (moved to last) */}
        <div>
          <label className="label">Notes (optional)</label>
          <textarea
            value={form.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={3}
            className="input resize-none"
            placeholder="Special instructions, gate codes, etc."
          />
        </div>

        {errors.submit && (
          <div
            className="bg-red-50 p-3 text-sm text-red-700"
            style={{ borderRadius: 'var(--radius-md)' }}
          >
            {errors.submit}
          </div>
        )}
      </div>
    </Slideover>
  );
}

